import type { AskApproval, AskApprovalKind, MessageBlock } from "@rakazo/contracts";
import {
  CARD_TITLE_ESCAPED_CHARACTERS,
  COMPOSIO_MULTI_EXECUTE_TOOL,
  isApprovalAskBlock,
} from "./action-approval.js";
import type { ToolApp, ToolDisplay } from "./tool-display.js";
import { describeToolCall, toolCallRef } from "./tool-display.js";

type AskBlock = Extract<MessageBlock, { kind: "ask" }>;

/** The label the user's-computer card gave its allow before `approval.allowScope` existed. */
export const ALLOW_FOR_CHAT_LABEL = "Allow for this chat";

/** The note under a create-space card, which clients word in their own language. */
export const CREATE_SPACE_APPROVAL_NOTE =
  "Bots, groups, chats, files, memory, and integrations in this space stay separate from other spaces.";

/** Whether an approval's allow covers the rest of this chat rather than one call. */
export function askAllowsChat(block: Pick<AskBlock, "approval" | "actions">): boolean {
  if (block.approval) return block.approval.allowScope === "chat";
  return block.actions?.some((action) => action.label === ALLOW_FOR_CHAT_LABEL) ?? false;
}

/**
 * What an approval card asks: its own `approval`, or, on a card stored before that existed, the
 * one its English title was written from. A title no template wrote reads as its text.
 */
export function askApproval(
  block: Pick<
    AskBlock,
    "kind" | "text" | "approval" | "approvalEffectId" | "actions" | "instructionUpdate"
  >,
): AskApproval | undefined {
  if (!isApprovalAskBlock(block) || block.instructionUpdate) return undefined;
  return block.approval ?? approvalFromTitle(block.text);
}

/** A tool card's call as its title names it, and how many more actions the batch runs. */
export type ApprovalToolCall = {
  display: ToolDisplay;
  more: number;
  /** The call's own name, for the detail, when the title names it in words instead. */
  rawName?: string;
};

export function describeApprovalTool(
  tool: NonNullable<AskApproval["tool"]>,
  actionCount: number | undefined,
  apps: readonly ToolApp[],
): ApprovalToolCall {
  const display = describeToolCall(tool, apps);
  const actions = tool.actions?.length ?? 0;
  const more = actions > 0 ? Math.max((actionCount ?? actions) - 1, 0) : 0;
  const inWords =
    display.kind === "app_action" ||
    display.kind === "app" ||
    (display.kind === "mcp" && Boolean(display.verb && display.object));
  // A batch card's detail already lists each action it runs.
  return inWords && actions === 0 ? { display, more, rawName: tool.name } : { display, more };
}

/** Fixed lines the server writes under an approval title, by the English it writes them in. */
export const APPROVAL_DETAIL_TEMPLATES = [
  CREATE_SPACE_APPROVAL_NOTE,
  "Members: {names}",
  "Rename to “{name}”",
  "Add: {names}",
  "Remove: {names}",
  "From: Default model",
  "To: Default model",
  "From: {model}",
  "To: {model}",
  "Thinking: Default",
  "Thinking: {level}",
  "Runs at {when}",
  "Repeats {cron}, next at {when}",
  "Prompt: {prompt}",
  "Old length: {count} characters",
  "New length: {count} characters",
] as const;
export type ApprovalDetailTemplate = (typeof APPROVAL_DETAIL_TEMPLATES)[number];

/** A detail line: one a fixed template wrote, with the values it holds, or text kept as is. */
export type ApprovalDetailLine =
  | { template: ApprovalDetailTemplate; values: Record<string, string> }
  | { text: string };

/**
 * Which templates a card's detail can hold, and in how many leading lines: the rest of an
 * instruction or routine card is the text being approved, never reworded.
 */
const DETAIL_LINES: Partial<
  Record<AskApprovalKind, { templates: readonly ApprovalDetailTemplate[]; lines?: number }>
> = {
  create_space: { templates: [CREATE_SPACE_APPROVAL_NOTE] },
  fleet_create_group: { templates: ["Members: {names}"] },
  fleet_update_group: { templates: ["Rename to “{name}”", "Add: {names}", "Remove: {names}"] },
  fleet_set_model: {
    templates: [
      "From: Default model",
      "To: Default model",
      "From: {model}",
      "To: {model}",
      "Thinking: Default",
      "Thinking: {level}",
    ],
  },
  fleet_schedule_routine: {
    templates: ["Runs at {when}", "Repeats {cron}, next at {when}", "Prompt: {prompt}"],
    lines: 2,
  },
  fleet_set_instructions: {
    templates: ["Old length: {count} characters", "New length: {count} characters"],
    lines: 2,
  },
};

const TEMPLATE_PATTERNS = new Map<ApprovalDetailTemplate, { pattern: RegExp; keys: string[] }>(
  APPROVAL_DETAIL_TEMPLATES.map((template) => {
    const keys = [...template.matchAll(/\{(\w+)\}/g)].map((match) => match[1] ?? "");
    const source = template
      .split(/\{\w+\}/)
      .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("(.*)");
    return [template, { pattern: new RegExp(`^${source}$`), keys }];
  }),
);

/**
 * A card's detail as lines, with the ones a fixed template wrote marked so clients can word them.
 * `rawName` goes first when the detail does not show it yet.
 */
export function approvalDetailLines(
  kind: AskApprovalKind,
  detail: string | undefined,
  rawName?: string,
): ApprovalDetailLine[] {
  const lines = detail ? detail.split("\n") : [];
  const read = DETAIL_LINES[kind];
  const parsed = lines.map((line, index): ApprovalDetailLine => {
    if (!read || (read.lines !== undefined && index >= read.lines)) return { text: line };
    for (const template of read.templates) {
      const { pattern, keys } = TEMPLATE_PATTERNS.get(template)!;
      const match = line.match(pattern);
      if (!match) continue;
      return {
        template,
        values: Object.fromEntries(keys.map((key, at) => [key, match[at + 1] ?? ""])),
      };
    }
    return { text: line };
  });
  return rawName && !lines.includes(rawName) ? [{ text: rawName }, ...parsed] : parsed;
}

const ESCAPED = new RegExp(`\\\\(${CARD_TITLE_ESCAPED_CHARACTERS})`, "g");
/** A name an approval title escaped for Markdown, as plain text. */
const plain = (name: string) => name.replace(ESCAPED, "$1");

const TITLES: ReadonlyArray<readonly [RegExp, (match: RegExpMatchArray) => AskApproval]> = [
  [/^Run on your computer\?$/, () => ({ kind: "user_computer" })],
  [
    /^(Approve|Decline) connection with “(.+)”\?$/s,
    (match) => ({
      kind: match[1] === "Approve" ? "agent_connection_approve" : "agent_connection_decline",
      name: plain(match[2] ?? ""),
    }),
  ],
  // The space card's name was never escaped.
  [/^Create space “(.*)”\?$/s, (match) => ({ kind: "create_space", name: match[1] ?? "" })],
  [
    /^Create group “(.+)”\?$/s,
    (match) => ({ kind: "fleet_create_group", name: plain(match[1] ?? "") }),
  ],
  [
    /^Update group “(.+)”\?$/s,
    (match) => ({ kind: "fleet_update_group", name: plain(match[1] ?? "") }),
  ],
  [
    /^Change the model of (.+)\?$/s,
    (match) => ({ kind: "fleet_set_model", name: plain(match[1] ?? "") }),
  ],
  [
    /^Schedule “(.+?)” for (.+)\?$/s,
    (match) => ({
      kind: "fleet_schedule_routine",
      subject: plain(match[1] ?? ""),
      name: plain(match[2] ?? ""),
    }),
  ],
  [
    /^Replace the instructions of (.+)\?$/s,
    (match) => ({ kind: "fleet_set_instructions", name: plain(match[1] ?? "") }),
  ],
];

const REVIEW_BEFORE = "Review before ";
const TOOL_NAME = /^[A-Za-z0-9][A-Za-z0-9_.:-]*$/;

function approvalFromTitle(text: string): AskApproval | undefined {
  for (const [pattern, approval] of TITLES) {
    const match = text.match(pattern);
    if (match) return approval(match);
  }
  return text.startsWith(REVIEW_BEFORE)
    ? approvalFromSummary(text.slice(REVIEW_BEFORE.length))
    : undefined;
}

/** `Review before {summary}`: a bot card, a batch of actions, or a call with what it acts on. */
function approvalFromSummary(summary: string): AskApproval | undefined {
  const bot = summary.match(/^(delete|archive) bot(?: \((.+)\))?$/s);
  if (bot) {
    return {
      kind: bot[1] === "delete" ? "delete_bot" : "archive_bot",
      ...(bot[2] ? { name: bot[2] } : {}),
    };
  }
  const arrow = summary.indexOf(" → ");
  const call = arrow < 0 ? summary : summary.slice(0, arrow);
  const target = arrow < 0 ? undefined : summary.slice(arrow + " → ".length);
  // A cut-off title ends in a name no tool has, so it reads as its text.
  const names = call.split(", ");
  if (!names.every((name) => name === "…" || TOOL_NAME.test(name))) return undefined;
  if (names.length > 1 || names[0] === "…") {
    if (target !== undefined) return undefined;
    const actions = names.filter((name) => name !== "…");
    const tool = toolCallRef(COMPOSIO_MULTI_EXECUTE_TOOL, { actions });
    return tool ? { kind: "tool", tool, actionCount: names.length } : undefined;
  }
  const name = names[0] ?? "";
  if (name === "web_fetch" && target) return { kind: "site", site: target };
  return { kind: "tool", tool: { name }, ...(target ? { target } : {}) };
}
